/**
 * AudioPads - the soundboard on the stage (2026-10-13): one pad or a grid, each playing a library file through
 * OPBridge (`audio/pads.ts`). Cells are the Button Grid's skinned cells (`bg-grid` / `bg-cell`), so every skin and
 * colour-as-light (DEC-271) carries over with no new material.
 *
 * - Lit (`data-active`) = OPBridge says it is sounding; the amber rim (`data-armed`) = waiting for the beat or bar,
 *   or the guard's first tap. A line along the bottom is how far through the file it is.
 * - A loop pad is a toggle; a gate pad plays while held; a one-shot restarts on a re-press.
 * - Drop an audio file on a pad (like Ableton's drum rack): it imports into the library and the pad takes it.
 */
import { createEffect, createMemo, createSignal, For, onCleanup, Show, untrack, type Accessor } from 'solid-js';
import { editItem, editOwnsGesture, selectedItemId, type AudioPadsItem, type CellColor } from '../layout/store.ts';
import { createDiscreteGuard } from '../performance/guard.ts';
import {
  isPending, padId, padsUnavailable, preloadPads, pressPad, releasePad, resolvedPad, trackOf, voiceOf, watchPads,
} from '../audio/pads.ts';
import type { PadVoice } from '../opbridge/httpClient.ts';
import { enqueueAudio, looksLikeAudio } from '../timeline/testTrackStore.ts';
import { pushToast } from '../ui/toast.ts';

const CELL_COLOR_TOKEN: Record<CellColor, string> = {
  cyan: 'idle', magenta: 'incoming', amber: 'armed', green: 'ok', violet: 'violet',
  yellow: 'yellow', pink: 'pink', sky: 'sky', tan: 'tan', red: 'error',
};

const QUANT_WORD = { now: '', beat: 'on the beat', bar: 'on the bar' } as const;

export function AudioPads(props: { item: AudioPadsItem }) {
  const guard = createDiscreteGuard(() => !!props.item.guard);
  const pcKey = createMemo(() => props.item.pc);
  createEffect(() => { const release = watchPads(pcKey()); onCleanup(release); });
  createEffect(() => preloadPads(props.item));
  const [dropAt, setDropAt] = createSignal<number | null>(null);
  const down = new Map<number, number>();

  function onDown(i: number, e: PointerEvent) {
    if (editOwnsGesture(props.item.id)) return;
    if (selectedItemId() === props.item.id) return;
    if (padsUnavailable()) return;
    e.stopPropagation();
    if (!guard.gate(String(i))) return;
    down.set(e.pointerId, i);
    void pressPad(props.item, i);
  }
  function onUp(e: PointerEvent) {
    const i = down.get(e.pointerId);
    if (i === undefined) return;
    down.delete(e.pointerId);
    if (resolvedPad(props.item.pads[i]).mode === 'gate') void releasePad(props.item, i);
  }

  const hasFiles = (e: DragEvent) => Array.from(e.dataTransfer?.types ?? []).includes('Files');
  async function onDrop(i: number, e: DragEvent) {
    if (!hasFiles(e)) return;
    e.preventDefault();
    e.stopPropagation();
    setDropAt(null);
    const file = Array.from(e.dataTransfer?.files ?? []).find(looksLikeAudio);
    if (!file) { pushToast('warn', 'That is not an audio file OPBridge can read'); return; }
    const [t] = await enqueueAudio([file]);
    if (!t) return;
    const pads = props.item.pads.slice();
    pads[i] = { ...pads[i], trackId: t.id };
    editItem(props.item.id, { pads });
  }

  return (
    <div
      class="bg-grid aud-pads"
      style={{ '--bg-cols': props.item.cols, '--bg-rows': props.item.rows, width: `${props.item.w}px`, height: `${props.item.h}px` }}
      data-disabled={padsUnavailable() ? 'true' : 'false'}
    >
      <For each={props.item.pads}>
        {(pad, index) => {
          const id = () => padId(props.item, index());
          const voice = () => voiceOf(props.item.pc, id());
          const track = () => trackOf(pad);
          const r = () => resolvedPad(pad);
          const waiting = () => voice()?.state === 'waiting' || (!voice() && isPending(id()) && r().quant !== 'now');
          const sounding = () => !!voice() && voice()!.state !== 'waiting';
          const name = () => pad.label ?? track()?.name ?? (pad.trackId ? 'Missing file' : 'Empty');
          const sub = () => {
            const off = padsUnavailable();
            if (off) return off;
            if (!pad.trackId) return 'drop a file';
            if (guard.armedKey() === String(index())) return 'tap again';
            if (waiting()) return QUANT_WORD[r().quant] || 'starting';
            if (sounding()) return r().mode === 'loop' ? 'looping' : r().mode === 'gate' ? 'held' : 'playing';
            return r().mode === 'loop' ? 'loop' : r().mode === 'gate' ? 'hold' : QUANT_WORD[r().quant] || 'one-shot';
          };
          return (
            <button
              class="bg-cell aud-pad"
              type="button"
              data-testid="aud-pad"
              data-index={index()}
              data-color={CELL_COLOR_TOKEN[pad.color ?? 'cyan']}
              data-active={sounding() ? 'true' : 'false'}
              data-armed={waiting() || guard.armedKey() === String(index()) ? 'true' : 'false'}
              data-empty={pad.trackId ? 'false' : 'true'}
              data-drop={dropAt() === index() ? 'true' : 'false'}
              onPointerDown={(e) => onDown(index(), e)}
              onPointerUp={onUp}
              onPointerCancel={onUp}
              onDragOver={(e) => { if (hasFiles(e)) { e.preventDefault(); setDropAt(index()); } }}
              onDragLeave={() => setDropAt((d) => (d === index() ? null : d))}
              onDrop={(e) => void onDrop(index(), e)}
            >
              <span class="bg-cell-label aud-pad-name"><span class="face-line">{name()}</span></span>
              <Show when={!props.item.hideLabel}><span class="aud-pad-sub">{sub()}</span></Show>
              <Show when={voice()}>
                {(v) => <PadProgress voice={v} />}
              </Show>
            </button>
          );
        }}
      </For>
    </div>
  );
}

/**
 * The line along a pad's bottom, GLIDING every frame (operator, 2026-10-14: "we need to get it butter smooth").
 * OPBridge's status is polled every 150 ms; drawing each reply (through a 150 ms transition) stalled and jumped
 * whenever a reply came late. This runs a clock from the reply's `posSec` at the voice's own `rate` (1, or the
 * repitch rate), the way SongMarker's playhead does: a reply only nudges the SPEED (a reply is only ever late, so
 * pulling back to it would rubberband), and only a gap of 0.4 s or more (a re-press, a seek) moves it at once.
 * A loop wraps back to its start; a waiting voice sits still.
 */
function PadProgress(props: { voice: Accessor<PadVoice> }) {
  let el: HTMLSpanElement | undefined;
  let anchor: { at: number; sec: number; rate: number } | null = null;
  const lap = () => {
    const v = props.voice();
    const a = v.startSec ?? 0;
    const b = v.endSec ?? 0;
    return { a, len: b - a, loop: v.mode === 'loop' };
  };
  const glide = (now: number) => (anchor ? anchor.sec + Math.min(1.5, Math.max(0, (now - anchor.at) / 1000)) * anchor.rate : null);
  /** File seconds -> 0..1 through the lap (a loop wraps). */
  const frac = (sec: number) => {
    const { a, len, loop } = lap();
    if (!(len > 0)) return 0;
    const x = (sec - a) / len;
    return loop ? x - Math.floor(x) : Math.max(0, Math.min(1, x));
  };
  createEffect(() => {
    const v = props.voice();
    const base = typeof v.rate === 'number' && v.rate > 0 ? v.rate : 0;
    const { a, len, loop } = untrack(lap);
    if (v.state === 'waiting' || base === 0 || !(len > 0)) {
      anchor = null;
      el?.style.setProperty('--p', String(Math.max(0, Math.min(1, v.progress))));
      return;
    }
    const now = performance.now();
    const p = glide(now);
    let err = Infinity;
    if (p != null) {
      // Compare inside the lap: a loop's reply has wrapped while the glide runs on.
      const wrapped = loop ? a + (((p - a) % len) + len) % len : p;
      err = v.posSec - wrapped;
      if (loop && Math.abs(err) > len / 2) err -= len * Math.sign(err);
    }
    if (!Number.isFinite(err) || Math.abs(err) >= 0.4) { anchor = { at: now, sec: v.posSec, rate: base }; return; }
    // Behind the report: speed up (up to 10%). Ahead by under 150 ms: a late reply, ease off barely (3% at most);
    // ahead by more: slow down (up to 10%). Never below 0.9 of the voice's rate: it always moves forward.
    const k = err >= 0 ? 1 + Math.min(0.1, err / 0.5)
      : err > -0.15 ? 1 - Math.min(0.03, -err / 2)
      : 1 - Math.min(0.1, -err / 0.5);
    anchor = { at: now, sec: p!, rate: base * k };
  });
  let raf = 0;
  const step = () => {
    const now = performance.now();
    const sec = glide(now);
    if (sec != null && el) el.style.setProperty('--p', String(frac(sec)));
    raf = requestAnimationFrame(step);
  };
  raf = requestAnimationFrame(step);
  onCleanup(() => cancelAnimationFrame(raf));
  return <span ref={el} class="aud-pad-progress" style={{ '--p': String(Math.max(0, Math.min(1, untrack(() => props.voice().progress)))) }} />;
}
